/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { RadioCard, RadioGroup, Typography, useTheme } from '@coursera/cds-core';
import type { ViewForm as EventDataViewForm } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { Spacer } from 'bundles/enterprise-ui';
import type { SurveyQuestion } from 'bundles/onboarding-2022/types/Survey';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/program-home';

const TrackedRadioCard = withSingleTracked({ type: 'CHECKBOX' })(RadioCard);

export type PropsFromCaller = {
  onLevelChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  surveyData?: SurveyQuestion;
  selectedLevel?: LearnerLevels;
  label: string;
  sharedEventingV3Data: Omit<EventDataViewForm, 'page' | 'user_properties'>;
};

const radioGroupStyle = (theme: Theme) =>
  css({
    width: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    [theme.breakpoints.down('xs')]: {
      width: '360px',
    },
  });

// eslint-disable-next-line no-restricted-syntax
export enum LearnerLevels {
  BEGINNER = 'ONBOARDING_LEVEL_BEGINNER',
  INTERMEDIATE = 'ONBOARDING_LEVEL_INTERMEDIATE',
  ADVANCED = 'ONBOARDING_LEVEL_ADVANCED',
  UNSURE = 'ONBOARDING_LEVEL_UNSURE',
}

export const LevelSelectionStep = ({
  onLevelChange,
  surveyData,
  selectedLevel,
  label,
  sharedEventingV3Data,
}: PropsFromCaller) => {
  const theme = useTheme();
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_form', {
    ...sharedEventingV3Data,
    form: { ...sharedEventingV3Data.form, fieldsAvailable: 1 },
  });

  const SurveyLevelData = {
    [LearnerLevels.BEGINNER]: {
      label: _t('Beginner'),
      description: _t('I have little to no experience in the field.'),
    },
    [LearnerLevels.INTERMEDIATE]: {
      label: _t('Intermediate'),
      description: _t('I have experience but need more practice.'),
    },
    [LearnerLevels.ADVANCED]: {
      label: _t('Advanced'),
      description: _t('I have deep knowledge and solid experience.'),
    },
    [LearnerLevels.UNSURE]: {
      label: _t('Not sure'),
      description: _t('I have a blend of experience levels and depth of knowledge.'),
    },
  };

  return (
    <TrackedDiv
      trackingName="level_selection_step"
      data-testid="level-selection-step"
      requireFullyVisible={false}
      withVisibilityTracking
      trackClicks={false}
    >
      {/* Additional div as a workaround to get visibility tracking to work for eventing v3 */}
      <div ref={visibilityTrackingRef}>
        <Typography component="h1" variant="h1semibold">
          {label}
        </Typography>
        <Spacer size={12} />
        <RadioGroup
          aria-label={_t('Select your level')}
          name="level-selection"
          onChange={onLevelChange}
          css={radioGroupStyle(theme)}
          value={selectedLevel}
        >
          {surveyData?.options.map((level) => (
            <TrackedRadioCard
              key={level.id}
              trackingName={SurveyLevelData[level.id as LearnerLevels].label}
              label={SurveyLevelData[level.id as LearnerLevels].label}
              value={level.id}
              supportText={SurveyLevelData[level.id as LearnerLevels].description}
            />
          ))}
        </RadioGroup>
        <Spacer size={48} />
      </div>
    </TrackedDiv>
  );
};
